import React, { useContext, useEffect, useState, useMemo } from "react";
import { Typography, Box, Grid, Paper, Avatar, TextField, Button, CircularProgress } from '@mui/material';
import { AuthContext } from "../components/Auth";
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

const PAGE_SIZE = 12;

const ChannelsPage = () => {
    const { firestoreInstance } = useContext(AuthContext);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    useEffect(() => {
        if (!firestoreInstance) return;
        let mounted = true;
        const fetchChannels = async () => {
            setLoading(true);
            setError(null);
            try {
                const q = query(collection(firestoreInstance, 'channels'), orderBy('created_at', 'desc'));
                const snap = await getDocs(q);
                const items = snap.docs.map(d => {
                    const data = d.data() || {};
                    const creator = data.creator_info || { displayName: data.displayName, profileImageUrl: data.profileImageUrl, email: data.email };
                    return {
                        id: d.id,
                        title: data.title || data.displayName || 'Untitled',
                        image: data.image_url || data.profileImageUrl || creator.profileImageUrl || '',
                        creatorDisplayName: creator.displayName || creator.email || '',
                        creatorAvatar: creator.profileImageUrl || '',
                        followersCount: data.followersCount || 0,
                        createdAt: data.created_at ? (data.created_at.toDate ? data.created_at.toDate().toLocaleDateString() : String(data.created_at)) : '' ,
                        description: data.description || ''
                    };
                });
                if (mounted) setChannels(items);
            } catch (e) {
                console.error(e);
                if (mounted) setError('Failed to load channels');
            } finally {
                if (mounted) setLoading(false);
            }
        }
        fetchChannels();
        return () => { mounted = false };
    }, [firestoreInstance]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return channels;
        return channels.filter(c => (c.title || '').toLowerCase().includes(q) || (c.creatorDisplayName || '').toLowerCase().includes(q));
    }, [channels, search]);

    const visible = filtered.slice(0, visibleCount);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 800 }}>Channels</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField size="small" placeholder="Search channels or creators" value={search} onChange={e => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }} sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={() => { setSearch(''); setVisibleCount(PAGE_SIZE); }}>Clear</Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {visible.map(ch => (
                            <Grid key={ch.id} size={{ xs: 12, md: 3 }}>
                                <Paper component="a" href={`/channels/${ch.id}`} sx={{ display: 'block', p: 1.5, textDecoration: 'none', color: 'inherit' }}>
                                    <Box sx={{ height: 140, borderRadius: 1, backgroundImage: ch.image && ch.image.startsWith('http') ? `url(${ch.image})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', bgcolor: !(ch.image && ch.image.startsWith('http')) ? 'grey.800' : undefined }} />
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                        <Avatar src={ch.creatorAvatar} alt={ch.creatorDisplayName} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 700 }}>{ch.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{ch.creatorDisplayName} · {ch.followersCount} followers</Typography>
                                        </Box>
                                    </Box>
                                    {ch.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{ch.description}</Typography>}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {filtered.length > visibleCount && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button variant="contained" onClick={() => setVisibleCount(v => v + PAGE_SIZE)}>Load more</Button>
                        </Box>
                    )}
                    {filtered.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">No channels match your search.</Typography></Box>
                    )}
                </>
            )}
        </Box>
    );
}
export default ChannelsPage;